import { baseApi } from '@/shared/api/baseApi';
import { ACCOUNTING_TAGS } from '@/shared/api/tags';
import type {
  CounterpartyDto,
  CounterpartyLedgerEntry,
  CreateCounterpartyRequest,
  ReconciliationResult,
  UpdateCounterpartyRequest,
} from '../model/types';

const counterpartyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listCounterparties: builder.query<CounterpartyDto[], { q?: string; archived?: boolean } | void>({
      query: (params) => ({ url: 'counterparties', params: params ?? undefined }),
      providesTags: ['Counterparty'],
    }),
    createCounterparty: builder.mutation<CounterpartyDto, CreateCounterpartyRequest>({
      query: (body) => ({ url: 'counterparties', method: 'POST', body }),
      invalidatesTags: ['Counterparty'],
    }),
    getCounterparty: builder.query<CounterpartyDto, string>({
      query: (id) => `counterparties/${id}`,
      providesTags: ['Counterparty'],
    }),
    updateCounterparty: builder.mutation<CounterpartyDto, { id: string; body: UpdateCounterpartyRequest }>({
      query: ({ id, body }) => ({ url: `counterparties/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Counterparty'],
    }),
    archiveCounterparty: builder.mutation<CounterpartyDto, { id: string; archived: boolean }>({
      query: ({ id, archived }) => ({ url: `counterparties/${id}/${archived ? 'archive' : 'unarchive'}`, method: 'POST' }),
      invalidatesTags: ['Counterparty'],
    }),
    deleteCounterparty: builder.mutation<void, string>({
      query: (id) => ({ url: `counterparties/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Counterparty'],
    }),
    mergeCounterparty: builder.mutation<CounterpartyDto, { id: string; sourceId: string }>({
      query: ({ id, sourceId }) => ({ url: `counterparties/${id}/merge`, method: 'POST', body: { sourceId } }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    counterpartyLedger: builder.query<CounterpartyLedgerEntry[], string>({
      query: (id) => `counterparties/${id}/ledger`,
      providesTags: ['Counterparty'],
    }),
    counterpartyReconciliation: builder.query<ReconciliationResult, { id: string; from: string; to: string }>({
      query: ({ id, from, to }) => ({ url: `counterparties/${id}/reconciliation`, params: { from, to } }),
      providesTags: ['Counterparty', 'Report'],
    }),
    exportCounterpartyReconciliationXlsx: builder.query<Blob, { id: string; from: string; to: string }>({
      query: ({ id, from, to }) => ({
        url: `counterparties/${id}/reconciliation`,
        params: { from, to, format: 'xlsx' },
        responseHandler: (r: Response) => r.blob(),
      }),
    }),
  }),
});

export const {
  useListCounterpartiesQuery,
  useCreateCounterpartyMutation,
  useGetCounterpartyQuery,
  useUpdateCounterpartyMutation,
  useArchiveCounterpartyMutation,
  useDeleteCounterpartyMutation,
  useMergeCounterpartyMutation,
  useCounterpartyLedgerQuery,
  useCounterpartyReconciliationQuery,
  useLazyExportCounterpartyReconciliationXlsxQuery,
} = counterpartyApi;

export { counterpartyApi };
